import { useEffect, useState } from 'react';
import { useTheme, Tab, Tabs } from '@mui/material';
import { ExportCsv, Toolbar } from '@mui/x-data-grid-pro';
import Tooltip from '@mui/material/Tooltip';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Button } from '@mui/material';
import { PageCard } from '../../../../Compounds/CardWithTitle';
import { BasicDataGrid } from '../../../../Compounds/Table/BasicDataGrid';
import { DEFAULT_TABLE_STYLES, DEFAULT_TAB_SX_STYLES } from '../../../../../utils/styleHelpers';
import {
  getWizardDataSubject,
  bulkCreate,
  getWizardDataLoadingSubject,
  wizardDataCreateResponse
} from '../../../../../store/ProvisionalAsset';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { getColumnDefsByKey } from '../../ColumnDefs/PreviewProvisionalAssetsDefs';
import { fileNameHelper } from '../../../../../utils/fileNameHelper';

interface CompleteProvisionalAssetsStepProps {
  nextStep?: () => void;
  title: string;
}

export interface TabProps {
  name: string;
  key: string;
}

const toolbar = () => (
  <Toolbar>
    <Tooltip title="Download as CSV">
      <ExportCsv
        options={{ fileName: fileNameHelper('failed_created_provisional_assets') }}
        render={
          <Button
            data-cy="export-button"
            data-testid="export-button"
            startIcon={<FileDownloadIcon fontSize="small" />}
            sx={{ fontSize: '.8rem', color: 'secondary.main' }}>
            Export
          </Button>
        }
      />
    </Tooltip>
  </Toolbar>
);

const allTabs: TabProps[] = [
  { name: 'Success', key: 'success' },
  { name: 'Failed', key: 'failed' }
];

export const CompleteProvisionalAssetsStep = ({ title }: CompleteProvisionalAssetsStepProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stepData, setStepData] = useState<wizardDataCreateResponse | null>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const theme = useTheme();

  const runBulkCreate = async () => {
    await bulkCreate();
  };

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
  };

  const detectedSelectedTab = (newStepData: wizardDataCreateResponse) => {
    const indexTab = allTabs.findIndex((tab) => {
      return (newStepData[tab.key as keyof typeof newStepData] as [])?.length > 0;
    });

    return indexTab !== -1 ? indexTab : 0;
  };

  useEffect(() => {
    const wizardDataSub = getWizardDataSubject().subscribe((data) => {
      const newStepData = {
        success: data.success,
        failed: [...(data.customerErrors || []), ...(data.otherErrors || []), ...(data.productErrors || [])]
      };
      setSelectedTab(detectedSelectedTab(newStepData));
      setStepData(newStepData);
    });

    const loadingSub = getWizardDataLoadingSubject().subscribe((loading) => {
      setIsLoading(loading);
    });

    runBulkCreate();

    return () => {
      if (wizardDataSub) wizardDataSub.unsubscribe();
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, []);

  const sxStyle = {
    ...DEFAULT_TABLE_STYLES.sx,
    maxHeight: 'calc(100vh - 350px)',
    '.MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
      marginLeft: 'auto'
    },
    borderWidth: '0',
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0',
    borderBottomLeftRadius: '.75rem',
    borderBottomRightRadius: '.75rem'
  };

  const selectedTabKey = allTabs[selectedTab].key;
  const rows = stepData ? (stepData[selectedTabKey as keyof typeof stepData] as []) : [];
  const columns = getColumnDefsByKey(selectedTabKey);

  const tableSlots = selectedTab === 1 ? { toolbar } : undefined;

  return (
    <PageCard
      dataTestId="preview-prov-assets-step"
      dataCy="preview-prov-assets-step"
      childrenWrapperClass="max-w-[60vw] min-w-full"
      title={title}>
      <>
        {isLoading && (
          <div className="flex h-[10rem] m-auto">
            <ComponentSpinner label="Loading data..." />
          </div>
        )}
        {!isLoading && (
          <>
            <Tabs
              sx={DEFAULT_TAB_SX_STYLES}
              value={selectedTab}
              scrollButtons="auto"
              onChange={(_e, index) => {
                handleTabChange(index);
              }}
              slotProps={{
                indicator: {
                  sx: {
                    backgroundColor: theme.palette.primary.main,
                    height: 2,
                    borderRadius: 1
                  }
                }
              }}>
              {allTabs.map((tab, _index) => {
                const tabDataCount = stepData && (stepData[tab.key as keyof typeof stepData] as [])?.length;
                return (
                  <Tab
                    sx={{
                      color: 'grey9',
                      fontSize: '.8rem',
                      textTransform: 'none'
                    }}
                    key={tab.name}
                    label={`${tab.name} ${tabDataCount ? `(${tabDataCount})` : ''}`}
                  />
                );
              })}
            </Tabs>
            <BasicDataGrid
              key={selectedTab}
              rows={rows}
              columns={columns}
              sxStyle={sxStyle}
              disableColumnMenu
              showColumnVerticalBorder
              showCellVerticalBorder
              slots={tableSlots}
              showToolbar={!!tableSlots}
            />
          </>
        )}
      </>
    </PageCard>
  );
};
